import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, ChevronLeft, ChevronDown, Search, Sparkles, ArrowRight, Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import COUNTRIES from "@/lib/countries";
import { T, PrimaryBtn, NeonInput, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * Free Assessment — lead-capture form before the short Speaking mock.
 * Collects: full name, email, country, phone (with searchable country-code dropdown), target test.
 * On submit: POST /api/leads/free-assessment → forward to /free-assessment/speaking
 */
const EXAMS = [
  { id: "IELTS",  name: "IELTS" },
  { id: "PTE",    name: "PTE Academic" },
  { id: "ELLT",   name: "Oxford ELLT" },
  { id: "DUO",    name: "Duolingo" },
];

function CountryPicker({ value, onChange, dataTestid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return COUNTRIES;
    return COUNTRIES.filter((c) =>
      c.name.toLowerCase().includes(s) ||
      c.dial.replace("+","").includes(s.replace("+","")) ||
      c.code.toLowerCase() === s
    );
  }, [q]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={dataTestid}
        className="h-14 px-3 inline-flex items-center gap-2 rounded-sm font-mono transition-all whitespace-nowrap"
        style={{ background: T.input, border: `2px solid ${T.border}`, color: T.text }}
        onFocus={(e) => { e.target.style.borderColor = T.primary; e.target.style.boxShadow = `0 0 18px ${T.primaryGlow}`; }}
        onBlur={(e) => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; }}
      >
        <span className="text-lg leading-none">{value.flag}</span>
        <span className="text-sm">{value.dial}</span>
        <ChevronDown className="w-4 h-4 opacity-60" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-72 max-h-72 overflow-hidden rounded-sm shadow-lg"
          style={{ background: T.cardBg, border: `1px solid ${T.border}` }}>
          <div className="p-2 border-b" style={{ borderColor: T.borderSoft }}>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: T.textMuted }} />
              <input
                autoFocus
                placeholder="Search country or dial code…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="country-search"
                className="w-full h-10 pl-9 pr-3 rounded-sm font-mono text-sm outline-none"
                style={{ background: T.bg, border: `1px solid ${T.borderSoft}`, color: T.text }}
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-56">
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-center text-sm" style={{ color: T.textMuted }}>No matches</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => { onChange(c); setOpen(false); setQ(""); }}
                data-testid={`country-option-${c.code}`}
                className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-orange-50 transition-colors text-sm"
                style={{ color: T.text }}
              >
                <span className="text-lg">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="font-mono text-xs" style={{ color: T.textMuted }}>{c.dial}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FreeAssessmentLead() {
  const play = useClickSound();
  const nav = useNavigate();
  const [form, setForm] = useState({ fullName: "", email: "", country: COUNTRIES.find((c) => c.code === "GB"), phone: "", exam: "IELTS" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      // Persist lead — backend endpoint /api/leads/free-assessment
      try {
        await api.post("/leads/free-assessment", {
          full_name: form.fullName.trim(),
          email: form.email.trim().toLowerCase(),
          country_code: form.country.code,
          country_name: form.country.name,
          phone: `${form.country.dial} ${form.phone.trim()}`,
          target_exam: form.exam,
        });
      } catch (_) { /* if backend not yet ready, still let candidate start the assessment */ }
      // Save in session so the test page can show their name
      sessionStorage.setItem("freeAssessmentLead", JSON.stringify({
        full_name: form.fullName, email: form.email, exam: form.exam,
      }));
      toast.success("Lead saved — starting your free Speaking assessment.");
      setTimeout(() => nav("/free-assessment/speaking"), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the assessment.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen relative pb-20" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="free-assessment-lead-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>
      <AIPropsBackground />

      <header className="relative max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <AuthBrand />
        <Link to="/" onClick={() => play("hover")}
          className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline"
          style={{ color: T.textMuted }} data-testid="back-to-landing">
          <ChevronLeft className="w-3 h-3" /> BACK TO HOME
        </Link>
      </header>

      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 py-6">
        <div className="text-center mb-10">
          <div className="mono text-[10px] tracking-[0.3em] mb-3 inline-flex items-center gap-1" style={{ color: T.primary }}>
            <Sparkles className="w-3 h-3" /> // FREE FOREVER · NO CREDIT CARD
          </div>
          <h1 className="display text-4xl md:text-5xl font-bold leading-[1.05]">
            Your <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>free 5-minute</span><br />
            AI Speaking assessment.
          </h1>
          <p className="text-base mt-4 max-w-xl mx-auto" style={{ color: T.textMuted }}>
            Tell us a little about you — then say a few sentences to our AI examiner and get an instant predicted band score.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {["Your details", "Speaking mock", "Predicted band"].map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full flex items-center justify-center mono text-[10px] font-bold"
                style={{
                  background: i === 0 ? T.primary : T.cardBg,
                  color: i === 0 ? T.textOnPrimary : T.textMuted,
                  border: `1.5px solid ${i === 0 ? T.primary : T.border}`,
                  boxShadow: i === 0 ? `0 0 12px ${T.primaryGlow}` : "none",
                }}>
                {i + 1}
              </div>
              <div className="mono text-[10px] tracking-[0.25em]" style={{ color: i === 0 ? T.text : T.textMuted }}>{s.toUpperCase()}</div>
              {i < 2 && <div className="w-8 h-px mx-1" style={{ background: T.border }} />}
            </div>
          ))}
        </div>

        <HudFrame color={T.primary} className="p-1">
          <form onSubmit={submit} className="rounded-sm p-7 lg:p-9 space-y-5" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
            <NeonInput
              label="Full Name"
              icon={User}
              type="text"
              placeholder="Alex Kaur"
              value={form.fullName}
              onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
              data-testid="fa-name"
              autoComplete="name"
              required
            />
            <NeonInput
              label="Email Address"
              icon={Mail}
              type="email"
              placeholder="alex@quickmail.com"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="fa-email"
              autoComplete="email"
              required
            />

            {/* Country + phone combined */}
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] mb-2 font-bold" style={{ color: T.primary }}>
                Country &amp; Phone
              </div>
              <div className="flex gap-2 items-start">
                <CountryPicker value={form.country} onChange={(c) => setForm((f) => ({ ...f, country: c }))} dataTestid="fa-country-btn" />
                <div className="relative flex-1">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: T.textMuted }} />
                  <input
                    type="tel"
                    placeholder="20 1234 5678"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/[^\d\s-]/g, "") }))}
                    data-testid="fa-phone"
                    autoComplete="tel"
                    required
                    className="w-full h-14 rounded-sm font-mono px-4 pl-12 outline-none transition-all text-base"
                    style={{ background: T.input, border: `2px solid ${T.border}`, color: T.text }}
                    onFocus={(e) => { e.target.style.borderColor = T.primary; e.target.style.boxShadow = `0 0 18px ${T.primaryGlow}`; }}
                    onBlur={(e) => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; }}
                  />
                </div>
              </div>
              <div className="mt-1.5 mono text-[10px] tracking-[0.2em]" style={{ color: T.textMuted }}>
                Selected: {form.country.flag} {form.country.name} ({form.country.dial})
              </div>
            </div>

            {/* Exam picker */}
            <div>
              <div className="mono text-[10px] uppercase tracking-[0.3em] mb-2 font-bold" style={{ color: T.primary }}>
                Which exam are you preparing for?
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {EXAMS.map((e) => {
                  const active = form.exam === e.id;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => { play("primary"); setForm((f) => ({ ...f, exam: e.id })); }}
                      data-testid={`fa-exam-${e.id.toLowerCase()}`}
                      className="relative h-14 rounded-sm transition-all duration-300 active:scale-[0.97]"
                      style={{
                        background: active ? T.primary : T.cardBg,
                        color: active ? T.textOnPrimary : T.text,
                        border: `2px solid ${active ? T.primary : T.border}`,
                        boxShadow: active ? `0 0 14px ${T.primaryGlow}` : "none",
                        fontFamily: "'Rajdhani', sans-serif",
                      }}
                    >
                      <span className="font-bold tracking-[0.15em] text-sm">{e.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <PrimaryBtn type="submit" className="w-full h-16 text-base" disabled={loading} data-testid="fa-submit">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Saving…</> : <>
                <Mic className="w-5 h-5" />
                START SPEAKING ASSESSMENT
                <ArrowRight className="w-5 h-5" />
              </>}
            </PrimaryBtn>

            <div className="mono text-[10px] tracking-[0.3em] text-center" style={{ color: T.textMuted }}>
              // WE ONLY USE THIS TO EMAIL YOUR REPORT · UNSUBSCRIBE ANYTIME
            </div>
          </form>
        </HudFrame>
      </div>
    </div>
  );
}
